import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { errorText } from '../admin/api';
import { Card, Field, SaveBar, Tag, Toggle, confirmAction, formatDateTime, useSaver } from '../admin/ui';
import { Kicker, Link } from '../components';
import { plural } from '../format';
import { navigate, usePath } from '../router';
import { applyTheme } from '../theme';
import { operatorApi, type InquiryView, type PlatformStatusView, type TenantOverview } from './api';
import '../admin/admin.css';

/**
 * Die Hausverwaltung (Konzept 6) unter verwaltung.<Plattform>: Anbieter anlegen, Domains, Kontingente, Status,
 * Verwalter-Zugänge, Zustand der Plattform. Sieht keine Kundendaten – nur Zählwerte.
 */

type Route = { page: 'tenants' } | { page: 'tenant'; id: string } | { page: 'new' } | { page: 'inquiries' } | { page: 'status' } | { page: 'account' };

function routeFor(pathname: string): Route {
  const p = pathname.replace(/\/+$/, '') || '/';
  let m: RegExpExecArray | null;
  if (p === '/neu') return { page: 'new' };
  if ((m = /^\/anbieter\/([0-9a-f-]{36})$/.exec(p))) return { page: 'tenant', id: m[1]! };
  if (p === '/anfragen') return { page: 'inquiries' };
  if (p === '/status') return { page: 'status' };
  if (p === '/konto') return { page: 'account' };
  return { page: 'tenants' };
}

export function OperatorApp() {
  const path = usePath();
  const route = routeFor(path);
  const [me, setMe] = useState<{ email: string; name: string | null } | null | undefined>(undefined);

  useEffect(() => {
    applyTheme({ scheme: 'dark', brand: '#f0b431', brand2: '#9c4f19', action: '#f0820a' });
    document.title = 'Hausverwaltung – Evercourse';
    operatorApi
      .me()
      .then((r) => setMe(r.operator))
      .catch(() => setMe(null));
  }, []);

  if (me === undefined) return null;
  if (me === null) return <OperatorLogin onLoggedIn={setMe} />;

  let content: React.ReactNode;
  switch (route.page) {
    case 'tenant':
      content = <TenantPage id={route.id} />;
      break;
    case 'new':
      content = <NewTenantPage />;
      break;
    case 'inquiries':
      content = <InquiriesPage />;
      break;
    case 'status':
      content = <StatusPage />;
      break;
    case 'account':
      content = <AccountPage email={me.email} />;
      break;
    default:
      content = <TenantsPage />;
  }

  return (
    <div className="adm" style={{ minHeight: '100dvh' }}>
      <aside className="adm__side" style={{ top: 0, height: '100dvh' }}>
        <div className="adm__brand">
          <Kicker>Hausverwaltung</Kicker>
          <strong>Evercourse</strong>
        </div>
        <nav className="adm__nav">
          <Link href="/" className={`adm__navlink${route.page === 'tenants' || route.page === 'tenant' || route.page === 'new' ? ' adm__navlink--active' : ''}`}>Anbieter</Link>
          <Link href="/anfragen" className={`adm__navlink${route.page === 'inquiries' ? ' adm__navlink--active' : ''}`}>Anfragen</Link>
          <Link href="/status" className={`adm__navlink${route.page === 'status' ? ' adm__navlink--active' : ''}`}>Zustand</Link>
          <Link href="/konto" className={`adm__navlink${route.page === 'account' ? ' adm__navlink--active' : ''}`}>Konto</Link>
        </nav>
        <div className="adm__foot">
          <span className="muted small">{me.email}</span>
          <button type="button" className="link-button small" onClick={() => void operatorApi.logout().then(() => setMe(null))}>Abmelden</button>
        </div>
      </aside>
      <main className="adm__main">{content}</main>
    </div>
  );
}

function OperatorLogin({ onLoggedIn }: { onLoggedIn: (o: { email: string; name: string | null }) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLoggedIn((await operatorApi.login(email, password)).operator);
    } catch (err) {
      setError(errorText(err, 'E-Mail oder Passwort stimmen nicht.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page page--narrow">
      <form className="card card--login form" onSubmit={submit}>
        <Kicker>Evercourse</Kicker>
        <h1 className="login__title">Hausverwaltung</h1>
        <div className="field">
          <label htmlFor="op-email">E-Mail-Adresse</label>
          <input id="op-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label htmlFor="op-password">Passwort</label>
          <input id="op-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button type="submit" className="button button--large" disabled={busy}>{busy ? 'Bitte warten …' : 'Anmelden'}</button>
      </form>
    </main>
  );
}

const hours = (s: number) => (s / 3600).toFixed(1).replace('.', ',');

function TenantsPage() {
  const [tenants, setTenants] = useState<TenantOverview[] | null>(null);
  useEffect(() => void operatorApi.listTenants().then(setTenants).catch(() => setTenants([])), []);
  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Anbieter</h1>
          <p className="muted">Jede Akademie ist eine eigene Wohnung im Haus: eigene Domain, eigene Kunden, eigenes Stripe-Konto.</p>
        </div>
        <Link href="/neu" className="button">Anbieter anlegen</Link>
      </div>
      {tenants === null ? <p className="muted">Wird geladen …</p> : (
        <div className="adm-list">
          {tenants.map((t) => (
            <div key={t.id} className="adm-row">
              <div className="adm-row__body">
                <Link href={`/anbieter/${t.id}`} className="adm-row__title">{t.name}</Link>
                <span className="muted small">
                  {t.customDomain ?? `${t.slug}.…`} · {t.publishedCourses} von {plural(t.courses, 'Kurs', 'Kursen')} veröffentlicht · {plural(t.customers, 'Kunde', 'Kunden')} · {hours(t.usedSeconds)} h Video{t.storageQuotaSeconds ? ` von ${hours(t.storageQuotaSeconds)}` : ''}
                </span>
              </div>
              {t.house && <Tag tone="brand">Haus-Anbieter</Tag>}
              <Tag tone={t.status === 'active' ? 'ok' : 'warn'}>{t.status === 'active' ? 'aktiv' : 'gesperrt'}</Tag>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function TenantPage({ id }: { id: string }) {
  const [tenant, setTenant] = useState<TenantOverview | null | undefined>(undefined);
  const reload = useCallback(() => void operatorApi.getTenant(id).then(setTenant).catch(() => setTenant(null)), [id]);
  useEffect(reload, [reload]);
  if (tenant === undefined) return <p className="muted">Wird geladen …</p>;
  if (tenant === null) return <><h1>Anbieter nicht gefunden</h1><Link href="/" className="button button--secondary">Zur Liste</Link></>;
  return (
    <>
      <div className="adm-head">
        <div>
          <Link href="/" className="muted small">← Alle Anbieter</Link>
          <h1>{tenant.name}</h1>
          <p className="muted">Angelegt {formatDateTime(tenant.createdAt)} · {plural(tenant.courses, 'Kurs', 'Kurse')} · {plural(tenant.customers, 'Kunde', 'Kunden')} · {hours(tenant.usedSeconds)} h Video · Stripe {tenant.stripeConnected ? 'verbunden' : 'nicht verbunden'}</p>
        </div>
        <Tag tone={tenant.status === 'active' ? 'ok' : 'warn'}>{tenant.status === 'active' ? 'aktiv' : 'gesperrt'}</Tag>
      </div>
      <TenantForm tenant={tenant} onChanged={setTenant} />
      <AdminsCard tenant={tenant} onChanged={setTenant} />
    </>
  );
}

function TenantForm({ tenant: t, onChanged }: { tenant: TenantOverview; onChanged: (t: TenantOverview) => void }) {
  const toForm = (x: TenantOverview) => ({ name: x.name, customDomain: x.customDomain ?? '', status: x.status, house: x.house, quota: x.storageQuotaSeconds ? String(Math.round(x.storageQuotaSeconds / 3600)) : '' });
  const [form, setForm] = useState(toForm(t));
  const saver = useSaver();
  useEffect(() => setForm(toForm(t)), [t]);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(t));
  return (
    <Card as="form" title="Anbieter" onSubmit={(e) => { e.preventDefault(); void saver.run(async () => onChanged(await operatorApi.updateTenant(t.id, { name: form.name, customDomain: form.customDomain || null, status: form.status, house: form.house, storageQuotaHours: form.quota ? Number(form.quota) : null }))); }}>
      <div className="grid2">
        <Field label="Name" id="t-name"><input id="t-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={200} /></Field>
        <Field label="Kurzadresse" id="t-slug" hint="Ergibt die Unterdomain der Plattform. Lässt sich nicht ändern."><input id="t-slug" type="text" value={t.slug} disabled /></Field>
        <Field label="Eigene Domain" id="t-domain" hint="Der Anbieter setzt bei seinem Domain-Anbieter einen A-Eintrag auf den Server; das Zertifikat holt die Plattform selbst.">
          <input id="t-domain" type="text" value={form.customDomain} onChange={(e) => setForm({ ...form, customDomain: e.target.value })} placeholder="akademie.beispiel.de" />
        </Field>
        <Field label="Videokontingent in Stunden" id="t-quota" hint={`Belegt: ${hours(t.usedSeconds)} h. Leer = unbegrenzt.`}>
          <input id="t-quota" type="number" min={0} value={form.quota} onChange={(e) => setForm({ ...form, quota: e.target.value })} />
        </Field>
        <Field label="Status" id="t-status" hint="Gesperrt: Kunden sehen eine Wartungsseite, nichts wird gelöscht.">
          <select id="t-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'suspended' })}>
            <option value="active">Aktiv</option>
            <option value="suspended">Gesperrt</option>
          </select>
        </Field>
        <div className="toggles">
          <Toggle checked={form.house} onChange={(v) => setForm({ ...form, house: v })} label="Haus-Anbieter" hint="Zahlt keine Monatsgebühr (Erfolgsmagneten, Balance Vital Dogs)." />
        </div>
      </div>
      <SaveBar saver={saver} dirty={dirty} />
    </Card>
  );
}

function AdminsCard({ tenant: t, onChanged }: { tenant: TenantOverview; onChanged: (t: TenantOverview) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<{ email: string; password: string } | null>(null);
  const saver = useSaver();
  return (
    <Card title="Verwalter-Zugänge">
      <p className="muted small">Wer sich in die Verwaltung dieses Anbieters einloggen darf. Ein neues Passwort wird einmal angezeigt – gib es dem Verwalter persönlich weiter.</p>
      {fresh && (
        <div className="hint"><strong>Zugang für {fresh.email}:</strong> Passwort <code className="keybox" style={{ display: 'inline-block', padding: '4px 8px' }}>{fresh.password}</code> – wird nicht noch einmal angezeigt. Verwaltung: <code>{t.customDomain ? `https://${t.customDomain}` : `https://${t.slug}.…`}/verwaltung</code></div>
      )}
      <table className="table">
        <thead><tr><th>E-Mail</th><th>Name</th><th>Letzter Login</th><th></th></tr></thead>
        <tbody>
          {t.admins.map((a) => (
            <tr key={a.id}>
              <td><strong>{a.email}</strong></td>
              <td>{a.name ?? <span className="muted">–</span>}</td>
              <td className="muted">{formatDateTime(a.lastLoginAt)}</td>
              <td className="row-actions">
                <button type="button" className="link-button small" onClick={() => void saver.run(async () => { const r = await operatorApi.resetAdmin(t.id, a.email, a.name); setFresh({ email: a.email, password: r.password }); onChanged(r.tenant); })}>Neues Passwort</button>
                {t.admins.length > 1 && <button type="button" className="link-button small link-button--danger" onClick={() => { if (confirmAction(`Zugang ${a.email} entfernen?`)) void saver.run(async () => onChanged(await operatorApi.removeAdmin(t.id, a.id))); }}>Entfernen</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form className="grant" onSubmit={(e) => { e.preventDefault(); void saver.run(async () => { const r = await operatorApi.resetAdmin(t.id, email.trim(), name.trim() || null); setFresh({ email: email.trim(), password: r.password }); onChanged(r.tenant); setEmail(''); setName(''); }); }}>
        <span className="kicker">Verwalter hinzufügen</span>
        <div className="row">
          <input type="email" placeholder="E-Mail-Adresse" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input type="text" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="button" disabled={saver.busy || !email.trim()}>Anlegen</button>
        </div>
        {saver.error && <div className="error">{saver.error}</div>}
      </form>
    </Card>
  );
}

function NewTenantPage() {
  const [form, setForm] = useState({ name: '', slug: '', customDomain: '', house: false, quota: '10', scheme: 'dark', adminEmail: '', adminName: '' });
  const [result, setResult] = useState<{ tenant: TenantOverview; adminPassword: string } | null>(null);
  const saver = useSaver();
  const slugFromName = (name: string) => name.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

  if (result) {
    const t = result.tenant;
    return (
      <>
        <div className="adm-head"><div><h1>{t.name} ist angelegt</h1></div></div>
        <Card title="Zugangsdaten für den Verwalter – einmalig">
          <p>Verwaltung: <code>{t.customDomain ? `https://${t.customDomain}` : `https://${t.slug}.…`}/verwaltung</code></p>
          <p>E-Mail: <strong>{t.admins[0]?.email}</strong> · Passwort: <code className="keybox" style={{ display: 'inline-block', padding: '4px 8px' }}>{result.adminPassword}</code></p>
          <p className="muted small">Das Passwort wird nicht noch einmal angezeigt. Gib es persönlich weiter; der Verwalter ändert es in seinen Einstellungen.</p>
          {t.customDomain && <p className="muted small">Damit die Domain funktioniert, muss beim Domain-Anbieter ein A-Eintrag für <strong>{t.customDomain}</strong> auf die Server-Adresse zeigen. Das Zertifikat holt die Plattform automatisch.</p>}
          <div className="row-actions"><Link href={`/anbieter/${t.id}`} className="button">Zum Anbieter</Link><Link href="/" className="button button--secondary">Zur Liste</Link></div>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="adm-head"><div><Link href="/" className="muted small">← Alle Anbieter</Link><h1>Anbieter anlegen</h1></div></div>
      <Card as="form" onSubmit={(e) => { e.preventDefault(); void saver.run(async () => setResult(await operatorApi.createTenant({ name: form.name, slug: form.slug, customDomain: form.customDomain || null, house: form.house, storageQuotaHours: form.quota ? Number(form.quota) : null, scheme: form.scheme, adminEmail: form.adminEmail, adminName: form.adminName || null }))); }}>
        <div className="grid2">
          <Field label="Name der Akademie" id="n-name"><input id="n-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug === slugFromName(form.name) ? slugFromName(e.target.value) : form.slug })} required maxLength={200} /></Field>
          <Field label="Kurzadresse" id="n-slug" hint="Nur Kleinbuchstaben, Ziffern, Bindestriche. Ergibt die Unterdomain."><input id="n-slug" type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required pattern="[a-z0-9][a-z0-9-]{1,62}" /></Field>
          <Field label="Eigene Domain (optional)" id="n-domain"><input id="n-domain" type="text" value={form.customDomain} onChange={(e) => setForm({ ...form, customDomain: e.target.value })} placeholder="akademie.beispiel.de" /></Field>
          <Field label="Videokontingent in Stunden" id="n-quota" hint="Leer = unbegrenzt."><input id="n-quota" type="number" min={0} value={form.quota} onChange={(e) => setForm({ ...form, quota: e.target.value })} /></Field>
          <Field label="Grundton" id="n-scheme" hint="Der Anbieter kann es später selbst ändern.">
            <select id="n-scheme" value={form.scheme} onChange={(e) => setForm({ ...form, scheme: e.target.value })}><option value="dark">Dunkel</option><option value="light">Hell</option></select>
          </Field>
          <div className="toggles"><Toggle checked={form.house} onChange={(v) => setForm({ ...form, house: v })} label="Haus-Anbieter" hint="Zahlt keine Monatsgebühr." /></div>
          <Field label="E-Mail des ersten Verwalters" id="n-admin"><input id="n-admin" type="email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} required /></Field>
          <Field label="Name des Verwalters" id="n-adminname"><input id="n-adminname" type="text" value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} /></Field>
        </div>
        <SaveBar saver={saver} dirty={!!form.name && !!form.slug && !!form.adminEmail} label="Anbieter anlegen" />
      </Card>
    </>
  );
}

/** Anfragen über „Kostenlos testen“ auf evercourse.de – offen zuerst, erledigte darunter. */
function InquiriesPage() {
  const [items, setItems] = useState<InquiryView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => void operatorApi.listInquiries().then(setItems).catch((e) => { setItems([]); setError(errorText(e)); }), []);
  useEffect(reload, [reload]);
  const update = (id: string, patch: { handled?: boolean; note?: string | null }) =>
    void operatorApi.updateInquiry(id, patch).then((updated) => setItems((list) => (list ?? []).map((i) => (i.id === id ? updated : i)))).catch((e) => setError(errorText(e)));
  const remove = (i: InquiryView) => {
    if (!confirmAction(`Anfrage von ${i.name} endgültig löschen?`)) return;
    void operatorApi.deleteInquiry(i.id).then(() => setItems((list) => (list ?? []).filter((x) => x.id !== i.id))).catch((e) => setError(errorText(e)));
  };
  const open = (items ?? []).filter((i) => !i.handledAt);
  const done = (items ?? []).filter((i) => i.handledAt);
  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Anfragen</h1>
          <p className="muted">Wer auf evercourse.de „Kostenlos testen“ ausgefüllt hat. Erledigt heißt: Akademie angelegt oder geantwortet.</p>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {items === null ? <p className="muted">Wird geladen …</p> : items.length === 0 ? <p className="muted">Noch keine Anfragen.</p> : (
        <>
          {open.length > 0 && <InquiryList title={`${plural(open.length, 'offene Anfrage', 'offene Anfragen')}`} items={open} onUpdate={update} onRemove={remove} />}
          {done.length > 0 && <InquiryList title="Erledigt" items={done} onUpdate={update} onRemove={remove} />}
        </>
      )}
    </>
  );
}

function InquiryList({ title, items, onUpdate, onRemove }: { title: string; items: InquiryView[]; onUpdate: (id: string, patch: { handled?: boolean; note?: string | null }) => void; onRemove: (i: InquiryView) => void }) {
  return (
    <Card title={title}>
      <div className="adm-list">
        {items.map((i) => (
          <div key={i.id} className="adm-row" style={{ alignItems: 'flex-start' }}>
            <div className="adm-row__body" style={{ gap: 6 }}>
              <span className="adm-row__title">
                {i.name} · <a href={`mailto:${i.email}?subject=${encodeURIComponent('Dein Evercourse-Testzugang')}`}>{i.email}</a>
              </span>
              <span><strong>Thema:</strong> {i.topic}</span>
              {i.message && <span className="muted" style={{ whiteSpace: 'pre-wrap' }}>{i.message}</span>}
              <span className="muted small">Eingegangen {formatDateTime(i.createdAt)}{i.handledAt ? ` · erledigt ${formatDateTime(i.handledAt)}` : ''}</span>
              <InquiryNote inquiry={i} onSave={(note) => onUpdate(i.id, { note })} />
            </div>
            <div className="row-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              <button type="button" className="button button--small" onClick={() => onUpdate(i.id, { handled: !i.handledAt })}>{i.handledAt ? 'Wieder öffnen' : 'Erledigt'}</button>
              <button type="button" className="link-button small link-button--danger" onClick={() => onRemove(i)}>Löschen</button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function InquiryNote({ inquiry, onSave }: { inquiry: InquiryView; onSave: (note: string | null) => void }) {
  const [note, setNote] = useState(inquiry.note ?? '');
  useEffect(() => setNote(inquiry.note ?? ''), [inquiry.note]);
  const dirty = note !== (inquiry.note ?? '');
  return (
    <div className="row" style={{ marginTop: 4 }}>
      <input type="text" placeholder="Notiz (nur für dich)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
      {dirty && <button type="button" className="button button--small button--secondary" onClick={() => onSave(note.trim() || null)}>Notiz speichern</button>}
    </div>
  );
}

function StatusPage() {
  const [status, setStatus] = useState<PlatformStatusView | null>(null);
  useEffect(() => {
    const load = () => void operatorApi.status().then(setStatus).catch(() => undefined);
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, []);
  if (!status) return <p className="muted">Wird geladen …</p>;
  const gb = (b: number) => (b / 1024 ** 3).toFixed(0);
  return (
    <>
      <div className="adm-head"><div><h1>Zustand</h1><p className="muted">Plattform {status.platformDomain} · alle 30 Sekunden aktualisiert</p></div><Tag tone={status.health.ok ? 'ok' : 'warn'}>{status.health.ok ? 'alles in Ordnung' : 'Störung'}</Tag></div>
      {status.health.warnings.length > 0 && <div className="warning">{status.health.warnings.map((w) => <div key={w}>{w}</div>)}</div>}
      <div className="kpis" style={{ margin: '8px 0 24px' }}>
        <div className="kpi"><span className="kpi__value">{status.platform.tenants}</span><span className="kpi__label">Anbieter</span></div>
        <div className="kpi"><span className="kpi__value">{status.platform.customers}</span><span className="kpi__label">Kunden</span></div>
        <div className="kpi"><span className="kpi__value">{hours(status.platform.usedSeconds)}</span><span className="kpi__label">Stunden Video</span></div>
        <div className="kpi"><span className="kpi__value">{status.platform.videosReady}</span><span className="kpi__label">Videos fertig</span></div>
        <div className="kpi"><span className="kpi__value">{status.platform.videosProcessing}</span><span className="kpi__label">in Verarbeitung</span></div>
        <div className="kpi"><span className="kpi__value">{status.platform.videosFailed}</span><span className="kpi__label">fehlgeschlagen</span></div>
        <div className="kpi"><span className="kpi__value">{status.platform.mailsQueued}</span><span className="kpi__label">Mails wartend</span></div>
        <div className="kpi"><span className="kpi__value">{status.platform.mailsFailed}</span><span className="kpi__label">Mails gescheitert</span></div>
      </div>
      <Card title="Server">
        {status.health.storage ? <p>Festplatte: <strong>{gb(status.health.storage.totalBytes - status.health.storage.freeBytes)} GB belegt</strong>, {gb(status.health.storage.freeBytes)} GB frei von {gb(status.health.storage.totalBytes)} GB.</p> : <p className="muted">Kein Dateispeicher gefunden.</p>}
        <p className="muted small">Arbeiter (letztes Lebenszeichen): {Object.entries(status.health.workers).map(([k, v]) => `${k}: ${formatDateTime(v)}`).join(' · ') || 'noch keins'}</p>
      </Card>
    </>
  );
}

function AccountPage({ email }: { email: string }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const saver = useSaver();
  return (
    <>
      <div className="adm-head"><div><h1>Konto</h1><p className="muted">{email}</p></div></div>
      <Card as="form" title="Passwort ändern" onSubmit={(e) => { e.preventDefault(); void saver.run(async () => { await operatorApi.changePassword(current, next); setCurrent(''); setNext(''); }); }}>
        <div className="grid2">
          <Field label="Aktuelles Passwort" id="opw-current"><input id="opw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required /></Field>
          <Field label="Neues Passwort" id="opw-next" hint="Mindestens 10 Zeichen."><input id="opw-next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} /></Field>
        </div>
        <SaveBar saver={saver} dirty={current.length > 0 && next.length >= 10} label="Passwort ändern" />
      </Card>
    </>
  );
}

export function isOperatorHost(hostname: string): boolean {
  return hostname.toLowerCase().startsWith('verwaltung.');
}

export { navigate };
